import { Button } from '@/components/ui/button'
import { OfferingAgePricingRange } from '@/domain/offerings/offering'
import { useAddAgePricing, useOfferingAgePricing, useUpdateAgePricing } from '@/hooks/api/useOfferingAgePricing'
import { AlertCircle, Save, Loader2, Pencil, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { AgePricingRangeForm } from './AgePricingRangeForm'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

interface AgePricingEditorProps {
  offeringId: string
  activePriceId?: string
  canManage: boolean
  onSuccess?: () => void
  onCancel?: () => void
}

export const AgePricingEditor = ({ offeringId, activePriceId, canManage, onSuccess, onCancel }: AgePricingEditorProps) => {
  const { data: agePricing, isLoading } = useOfferingAgePricing(offeringId)
  const { mutateAsync: addAgePricing, isPending: isAdding } = useAddAgePricing()
  const { mutateAsync: updateAgePricing, isPending: isUpdating } = useUpdateAgePricing()

  const [ranges, setRanges] = useState<OfferingAgePricingRange[]>([])
  const [hasChanges, setHasChanges] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    if (agePricing) {
      setRanges(agePricing.ranges)
      setHasChanges(false)
    }
  }, [agePricing])

  const handleRangesChange = (newRanges: OfferingAgePricingRange[]) => {
    setRanges(newRanges)
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      const priceId = agePricing?.offeringPriceHistoryId || activePriceId || 'null';
      
      if (agePricing) {
        await updateAgePricing({
          offeringId,
          priceId,
          data: { ranges }
        })
      } else {
        await addAgePricing({
          offeringId,
          priceId,
          data: { ranges, status: 'active' }
        })
      }
      setHasChanges(false)
      setIsEditing(false)
      onSuccess?.()
    } catch (error) {
      // Error handled by hook
    }
  }

  const handleCancelAction = () => {
    if (agePricing) {
      setRanges(agePricing.ranges)
    } else {
      setRanges([])
    }
    setHasChanges(false)
    setIsEditing(false)
    onCancel?.()
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Configuración de Precios por Edad</h3>
        {canManage && (
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsEditing(true)}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Editar
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCancelAction}
                  disabled={isAdding || isUpdating}
                >
                  <X className="mr-2 h-4 w-4" />
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={!hasChanges || isAdding || isUpdating}
                >
                  {(isAdding || isUpdating) ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  Guardar
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {!agePricing && !hasChanges && (
        <Alert variant="destructive" className="bg-amber-50 border-amber-200 text-amber-800">
          <AlertCircle className="h-4 w-4 !text-amber-800" />
          <AlertTitle>Configuración pendiente</AlertTitle>
          <AlertDescription>
            Este servicio tiene habilitado el precio por edad pero no tiene rangos definidos.
          </AlertDescription>
        </Alert>
      )}

      <AgePricingRangeForm
        ranges={ranges}
        onChange={handleRangesChange}
        readOnly={!isEditing}
      />
    </div>
  )
}
